// Boot helper. `startNewGame` deals a fresh hand via the engine and returns
// the live `MahjongState` the scene will hold. Seat 0 is the human (bottom of
// the table); seats 1–3 are bot placeholders — the bots don't act yet (that's
// T8), so a dealt hand is a fully renderable static table for T7's visual
// check. Kept thin so `main.ts` stays glue.

import { createHand, type MahjongState } from '../game/GameState.js';
import { SimplifiedRuleset } from '../game/ruleset/SimplifiedRuleset.js';
import type { Ruleset } from '../game/ruleset/types.js';
import type { Seat } from '../game/types.js';
import { mulberry32 } from '../utils/prng.js';

/** The local human always sits at seat 0 (rendered at the bottom edge). */
export const LOCAL_SEAT: Seat = 0;

const U32_MAX = 0xffffffff;

/** Seed for a fresh deal — wall-clock XOR a u32 of noise so two same-tick
 *  launches don't collide. */
export function freshSeed(): number {
  const t = Date.now() & U32_MAX;
  const r = Math.floor(mulberry32(t)() * (U32_MAX + 1));
  return (t ^ r) >>> 0;
}

/** Deal a fresh hand. Pass a `seed` for a deterministic deal (lessons/tests), a
 *  `ruleset` for the active variant (defaults to Simplified free play), and a
 *  `dealer` for match play's rotating dealer seat. */
export function startNewGame(
  seed?: number,
  ruleset: Ruleset = SimplifiedRuleset,
  dealer?: Seat,
): MahjongState {
  return createHand(seed ?? freshSeed(), ruleset, dealer);
}
